import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import SectionHeading from '@/components/landing/section-heading'

import { pillars } from '@/constants/content'

const AboutSection: FC = (): ReactNode => {
    return (
        <section id='about' aria-labelledby='about-title' className='scroll-mt-20 bg-white py-20 sm:py-24 lg:py-28'>
            <div className='mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-6 lg:grid-cols-2 lg:px-8'>
                <div className='relative pb-12 pr-8 sm:pb-16 sm:pr-14'>
                    <div aria-hidden='true' className='dot-grid absolute -left-5 -top-5 size-40 rounded-3xl' />

                    <div className='relative aspect-[4/3] overflow-hidden rounded-3xl bg-muted shadow-xl shadow-ink/10'>
                        <Image
                            src='/images/about-students.jpg'
                            alt="Three young professionals working together on laptops at a shared desk"
                            fill
                            sizes='(min-width: 1280px) 560px, (min-width: 1024px) 45vw, 92vw'
                            className='object-cover'
                        />
                    </div>

                    <div className='absolute bottom-0 right-0 w-[44%] overflow-hidden rounded-2xl border-4 border-white bg-muted shadow-xl shadow-ink/15'>
                        <div className='relative aspect-[4/5]'>
                            <Image
                                src='/images/mentor-session.jpg'
                                alt="A developer writing code on a large monitor"
                                fill
                                sizes='(min-width: 1024px) 260px, 44vw'
                                className='object-cover'
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <SectionHeading
                        id='about-title'
                        eyebrow="About us"
                        title="Train where real software gets built."
                        description="Webify IT Technologies Pvt Ltd is a full-stack web and software development company with 10+ years of experience designing, building and deploying digital solutions for clients in the USA, UK and India. Our internships put you inside that work."
                    />

                    <ul className='mt-10 space-y-6'>
                        {pillars.map((pillar) => (
                            <li key={pillar.title} className='flex gap-4'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary'>
                                    <pillar.icon className='size-5' aria-hidden='true' />
                                </span>

                                <div>
                                    <h3 className='text-base font-semibold text-foreground'>
                                        {pillar.title}
                                    </h3>

                                    <p className='mt-1 text-[15px] leading-relaxed text-muted-foreground'>
                                        {pillar.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <figure className='mt-10 rounded-2xl border-l-4 border-teal bg-secondary/70 px-6 py-5'>
                        <blockquote className='font-heading text-lg font-medium leading-snug text-foreground'>
                            “Your career is a journey, and we're here to guide you every step of the way.”
                        </blockquote>

                        <figcaption className='mt-2 text-sm text-muted-foreground'>
                            The Webify promise
                        </figcaption>
                    </figure>
                </div>
            </div>
        </section>
    )
}

export default AboutSection
